/**
 * AI Scribe Recording Types
 *
 * TypeScript interfaces for the AI Scribe recording feature.
 * Copied from the backend API reference (Section 2).
 * Do not transform or rename — these match the backend DTOs exactly.
 */

// ─── Enums and Literal Types ────────────────────────────────────────

/** Recording lifecycle status */
export type RecordingStatus =
  | 'draft'
  | 'started' // capture initiated ("Start recording"); pre-upload (LF-4841)
  | 'uploading'
  | 'processing'
  | 'ready'
  | 'imported'
  | 'dismissed'
  | 'failed';

/**
 * VALE-1575 — why a note-writing analysis pass landed `ready` carrying a
 * best-effort partial after its retries were exhausted on a degraded response.
 * Non-null drives the "this note may be incomplete" banner. Matches the backend
 * `DegradedAnalysisReason` exactly.
 */
export type DegradedAnalysisReason =
  | 'repaired'
  | 'route-incomplete'
  | 'truncated';

/**
 * Substage while status is 'processing' (null for all other statuses).
 * `awaiting_documents` is LF-3052's document-ready gate — the workflow has
 * finished transcription and is blocked until the lawyer crosses the gate
 * via POST /:id/confirm-documents.
 */
export type RecordingProcessingStage =
  | 'uploading'
  | 'transcribing'
  | 'awaiting_documents'
  | 'analysing';

/** Transcription quality rating derived from confidence metrics */
export type TranscriptionQuality = 'high' | 'medium' | 'low' | 'very_low';

/**
 * Who a checklist item or action item is assigned to.
 *
 * The **const is the source of truth and the type is derived from it**, so a
 * runtime guard that has to admit the assignees — the matter checklist's own
 * write schema does (VALE-1852) — reads a real list rather than a hand-typed
 * copy of this union. Same shape, and for the same reason, as
 * `INSTRUCTION_TYPE_VALUES` in `@/lib/services/ai-analysis/types`.
 */
export const ACTION_ITEM_ASSIGNEE_VALUES = [
  'lawyer',
  'client',
  'pending'
] as const;

export type ActionItemAssignee = (typeof ACTION_ITEM_ASSIGNEE_VALUES)[number];

/** Recording mode — determines consent, diarisation, and prompt behaviour */
export type RecordingMode = 'meeting' | 'dictation';

/** Match confidence when deduplicating against existing matter entities */
export type MatchConfidence = 'exact' | 'high' | 'medium' | 'low';

/**
 * Shared provenance taxonomies live in `lib/services/ai-analysis/types.ts`
 * after LF-2910. Re-exported here so existing `@/lib/services/recordings`
 * imports continue to work.
 */
export type {
  ConfidenceLevel,
  InstructionType,
  LegalPersonalRepresentativeRoleSlug,
  QuoteSource,
  ProvenanceFields
} from '@/lib/services/ai-analysis/types';

import type {
  ConfidenceLevel,
  InstructionType,
  LegalPersonalRepresentativeRoleSlug,
  PreparedEntitlement,
  QuoteSource
} from '@/lib/services/ai-analysis/types';

/**
 * The planning vocabulary, imported from the planning service rather than
 * restated here (VALE-450 / VALE-466). The extraction attributes facts by the
 * same slots, roles and document kinds the planning record is keyed by, so a
 * second copy of any of them would be a second definition of one domain.
 */
import type {
  PlanningAppointmentRole,
  PlanningAppointorSuccession,
  PlanningDocumentKind,
  PlanningFactSource,
  PlanningSupersedeIntent,
  PlanningTestatorSlot,
  PlanningTrustKind
} from '@/lib/services/planning/types';

/** Browser audio format (determines S3 content type and Transcribe config) */
export type AudioFormat = 'webm' | 'mp4' | 'mp3' | 'wav' | 'ogg' | 'm4a';

// ─── Recording (list item shape) ────────────────────────────────────

/**
 * A participant is a **contact**, not a firm user — `recordings.service.ts`
 * resolves `participantContactIds` and copies `contact.firstName` /
 * `contact.lastName` straight through. `contacts.first_name` / `last_name` are
 * nullable too (an organisation contact carries only `organisationName`, and the
 * create DTO requires *either* that *or* a name), so these are honestly nullable
 * (LF-6015). Neither shape carries an email, so there is nothing to fall back
 * to — the display decision belongs to the call site.
 */
export interface RecordingParticipant {
  id: number;
  firstName: string | null;
  lastName: string | null;
}

/** The firm user who recorded it — both name columns nullable (LF-6015). */
export interface RecordingCreatedBy {
  id: number;
  firstName: string | null;
  lastName: string | null;
}

export interface RecordingImportStats {
  contacts: number;
  inventoryItems: number;
  checklistItems: number;
}

/**
 * Classifier verdict for an orphan recording. `admin`/`mixed` are the "is
 * probate" verdicts that drive the Create-matter CTA; `planning`/`other` are
 * non-probate. `null` until classified, and `null` forever for pre-bound
 * recordings (they skip the classifier). LF-3712.
 */
export type RecordingMeetingType = 'admin' | 'planning' | 'mixed' | 'other';

/** Who set `meetingType` — the classifier (`ai`) or a lawyer override (`user`). */
export type RecordingMeetingTypeClassifiedBy = 'ai' | 'user';

/** How a recording became bound to a matter. `null` for orphans. */
export type RecordingMatterBindSource = 'created' | 'associated' | 'pre_bound';

/**
 * How far a recording's planning proposals have got (VALE-466).
 *
 * Three states rather than two, because "the solicitor has been through it and
 * not imported yet" is the one a coordinator most needs to tell apart from
 * "nobody has looked": the first needs a decision, the second needs a review.
 */
export type PlanningReviewState =
  /** Proposals are on the recording and nobody has touched them. */
  | 'pending_review'
  /** A human has edited the proposals, but they have not been imported. */
  | 'reviewed'
  /** The proposals have landed on a matter. */
  | 'imported';

/** Returned in list views and as the creation/action response */
export interface Recording {
  id: number;
  /** Parent matter ID. `null` for orphan (matterless) recordings (LF-3712). */
  matterId: number | null;
  /**
   * The pre-matter enquiry this recording was captured for (LEADS-28), or `null`.
   *
   * Orthogonal to `matterId` — a lead-linked recording is matterless. The
   * enquiry's own facts arrive separately on `RecordingDetail.lead`; this is only
   * the link.
   */
  leadId?: number | null;
  title: string;
  status: RecordingStatus;
  mode: RecordingMode;
  processingStage: RecordingProcessingStage | null;
  // ── General-purpose AI Scribe (orphan lifecycle, LF-3712) ────────────
  /** Classifier verdict. `null` until classified / for pre-bound recordings. */
  meetingType?: RecordingMeetingType | null;
  /** Who set `meetingType`. `null` when `meetingType` is `null`. */
  meetingTypeClassifiedBy?: RecordingMeetingTypeClassifiedBy | null;
  /**
   * `true` once a lawyer edits the title — auto-title generation stops
   * overwriting it. Drives the Auto/Edited title badge (FE-7).
   */
  titleIsManual?: boolean;
  /** Archive timestamp (ISO). Non-null ⇒ archived. Orthogonal to `status`. */
  archivedAt?: string | null;
  /** When the lawyer dismissed the Create-matter suggestion (ISO). Non-null ⇒ CTA hidden. */
  createMatterSuggestionDismissedAt?: string | null;
  /** How the recording became bound to a matter. `null` for orphans. */
  matterBindSource?: RecordingMatterBindSource | null;
  /** Bound matter's internal reference, for the list-row matter badge. `null` for orphans. */
  matterReference?: string | null;
  /**
   * Bound matter's type slug (e.g. `'estate-administration'`). Drives the
   * Probate badge for matter-bound recordings, which skip the classifier and
   * carry no `meetingType` of their own. `null` for orphans.
   */
  matterType?: string | null;
  /**
   * Server-computed: whether the FE should render the Create-matter CTA.
   * Single source of truth — do not re-derive from `meetingType`/`matterId`.
   */
  showCreateMatterCta?: boolean;
  /**
   * Server-computed: how far this recording's **planning** proposals have got
   * (VALE-466). `null` when the recording carries no planning extraction at all,
   * which is what lets a surface tell "a meeting with facts waiting" apart from
   * "a meeting that produced none".
   *
   * Single source of truth, like `showCreateMatterCta` — do not re-derive.
   * `imported` in particular is keyed on `importStats`, the same durable marker
   * the server's own apply guard uses, so it stays `imported` after a
   * re-analysis rather than inviting a second import the server would skip.
   */
  planningReviewState?: PlanningReviewState | null;
  /**
   * What kind of planning meeting this was ([EPLAN-22]). `null` on every
   * non-planning recording, and until the analysis pass has proposed one.
   *
   * **The durable answer, and the one to display.** The planning block carries
   * a `meetingKind` too, but that is only what the model proposed at the last
   * pass: a re-analysis overwrites the blob and "revert to AI" restores the
   * model's own answer, either of which would discard a lawyer's correction.
   * This field survives both.
   */
  planningMeetingKind?: PlanningMeetingKind | null;
  /**
   * Whether `planningMeetingKind` was proposed by the analysis pass (`ai`) or
   * set by a lawyer (`user`). A `user` value is never overwritten by a later
   * re-analysis.
   */
  planningMeetingKindSetBy?: RecordingMeetingTypeClassifiedBy | null;
  /**
   * LF-3052 document-ready gate — timestamp when the lawyer crossed the gate
   * during processing. `null` until they click Skip or Done.
   */
  documentsReadyConfirmedAt?: string | null;
  /** LF-3052 — which gate path the lawyer chose (audit / analytics). */
  documentsReadyChoice?: 'with_documents' | 'no_documents' | null;
  /**
   * LF-4609 — true when a Context-tab document was linked to this recording
   * after its analysis already completed (`ready`/`imported`). Drives the
   * "you've added new context" re-analyse nudge banner. Cleared back to
   * `false` whenever the recording is (re-)analysed.
   */
  contextChangedSinceAnalysis?: boolean;
  /**
   * VALE-1575 — non-null when this recording is `ready` but its note-writing
   * analysis exhausted its retries on a degraded response and it carries the
   * recovered best-effort partial. Drives the "this note may be incomplete,
   * review carefully / retry" banner. Null on every clean recording.
   */
  analysisDegradedReason?: DegradedAnalysisReason | null;
  participants: RecordingParticipant[] | null;
  /**
   * Free-text participant "speaker hint" names (functional spec §6.2) — a
   * lightweight hint, not contact entities. Used by orphan recordings that have
   * no matter contacts to reference; complements `participants`.
   */
  participantNames?: string[] | null;
  /**
   * Free-text lawyer-provided context/instructions, editable on the Context tab
   * before/during/after recording. Fed into the analysis prompt.
   */
  instructions?: string | null;
  consentConfirmedAt: string;
  duration: number | null;
  recordedAt: string;
  createdAt: string;
  createdBy: RecordingCreatedBy;
  uploadUrl?: string;
  transcriptionConfidence: number | null;
  transcriptionQuality: TranscriptionQuality | null;
  summaryPreview: string | null;
  importStats: RecordingImportStats | null;
}

// ─── RecordingDetail (full detail shape) ────────────────────────────

export interface TranscriptSegment {
  speakerLabel: string;
  /**
   * Participant name the LLM mapped this speaker to (D-30). Optional — set by
   * analysis when the speaker can be confidently attributed, absent otherwise.
   * The raw `speakerLabel` is always kept alongside. Preserved on every
   * transcript edit (per-segment editing re-sends the full segment verbatim),
   * so a lawyer's text correction never drops the speaker mapping.
   */
  speakerName?: string;
  startTime: number;
  endTime: number;
  text: string;
}

export interface RecordingTranscript {
  fullText: string;
  segments?: TranscriptSegment[];
}

/**
 * LF-3052: `ActionItem` retired alongside `aiSummary.actionItems`. Operational
 * tasks now flow exclusively through `aiExtractedData.checklistItems` and land
 * on the matter checklist on import. `ActionItemAssignee` survives — same
 * union used by `AiScribeExtractedChecklistItem`.
 */

/** How a summary section's lines are written and rendered (VALE-480). */
export type NoteSectionFormat = 'bullets' | 'paragraphs';

/**
 * One named section of the file note. The heading is **firm-authored** — it
 * comes from the note template the recording was generated against, not from a
 * fixed FE list, so nothing may derive UI copy from it (see
 * `ScribeSummarySections`).
 */
export interface RecordingAiSummarySection {
  heading: string;
  /**
   * The section's lines. For `format: 'bullets'` each entry is one bullet; for
   * `paragraphs` each entry is one paragraph. Lawyer-editable.
   */
  content: string[];
  format: NoteSectionFormat;
}

export interface RecordingAiSummary {
  /**
   * The note's ordered sections (VALE-480). Replaced the fixed `keyFacts` /
   * `clientInstructions` pair: those were cut on an *epistemic* axis (facts vs
   * instructions) while a lawyer files a note on a *topic* axis.
   */
  sections: RecordingAiSummarySection[];
  /**
   * The note template that produced these sections. Recorded for display and
   * audit; the authoritative selection for the *next* generation lives on the
   * recording server-side.
   */
  templateId?: string | null;
  templateName?: string | null;
  /** ISO timestamp of the last lawyer PATCH to this summary. */
  lastEditAt?: string | null;
}

// ─── AI Extracted Data ─────────────────────────────────────────────

export interface AiScribeExtractedContact {
  firstName: string;
  lastName: string;
  middleName?: string;
  organisationName?: string;
  /** @deprecated Retained for in-flight recordings processed before LF-2891.
   *  Prefer the boolean flags + legalPersonalRepresentativeRole below. */
  role?: string;
  isLegalPersonalRepresentative?: boolean;
  isBeneficiary?: boolean;
  legalPersonalRepresentativeRole?: LegalPersonalRepresentativeRoleSlug;
  relationshipToDeceased?: string;
  isOfAge?: boolean;
  dateOfBirth?: string;
  phone?: string;
  email?: string;
  address?: {
    streetAddress?: string;
    suburb?: string;
    state?: string;
    postcode?: string;
  };
  /** Global Contact.notes — identity-level facts (rare for scribe). */
  notes?: string;
  /** Meeting-specific ContactMatter.notes — preferred target for scribe-extracted context. */
  matterNotes?: string;
  confidence?: ConfidenceLevel;
  source?: QuoteSource;
}

export interface AiScribeExtractedInventoryItem {
  type: string;
  institutionName?: string;
  /**
   * Resolved institution, populated server-side for orphan recordings (LF-4013)
   * so the synthesised review cards render the bank name / share registry. The
   * matter-bound review gets this via `/preview-changes`; the orphan review
   * synthesises from this shape, so the BE resolves `institutionName` (and the
   * ticker→registry lookup for shares) onto each item in the recording detail.
   */
  institution?: InstitutionPreviewRef;
  estateReference?: string;
  isNonEstate?: boolean;
  itemInfo?: Record<string, unknown>;
  notes?: string;
  instructionType?: InstructionType;
  confidence?: ConfidenceLevel;
  source?: QuoteSource;
  /**
   * Who holds this asset, on an **estate-planning** meeting ([EPLAN-17]).
   * Absent on every administration route, which asks the opposite question (did
   * a joint tenant survive the deceased) in `itemInfo`.
   *
   * **An empty or absent list means UNATTRIBUTED, not "solely owned".** The
   * ownership mode decides whether the asset passes under a will at all, so a
   * question nobody answered must not read as an answer.
   */
  planningOwners?: AiScribePlanningAssetOwner[];
}

export interface AiScribeExtractedChecklistItem {
  text: string;
  assignee: ActionItemAssignee;
  instructionType?: InstructionType;
  confidence?: ConfidenceLevel;
  source?: QuoteSource;
}

export interface AiScribeExistingItemNote {
  entityType: 'contact' | 'inventoryItem';
  matchedEntityId: number;
  matchedEntityName: string;
  suggestedNote: string;
  instructionType?: InstructionType;
  confidence?: ConfidenceLevel;
  source?: QuoteSource;
}

export interface AiScribeExtractedDeceasedAddress {
  streetAddress?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  country?: string;
}

export interface AiScribeExtractedDeceased {
  title?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  dateOfBirth?: string;
  dateOfDeath?: string;
  address?: AiScribeExtractedDeceasedAddress;
  /** Matter-level estate notes extracted from the transcript (free text). Writes to `Matter.notes` on apply. */
  notes?: string;
  confidence?: ConfidenceLevel;
  source?: QuoteSource;
}

export interface RecordingAiExtractedData {
  contacts: AiScribeExtractedContact[];
  inventoryItems: AiScribeExtractedInventoryItem[];
  checklistItems: AiScribeExtractedChecklistItem[];
  existingItemNotes: AiScribeExistingItemNote[];
  deceased?: AiScribeExtractedDeceased | null;
  /** Will-derived entitlements — surfaced only when at least one recording-linked file is a will. */
  entitlements?: import('@/lib/services/ai-analysis/types').PreparedEntitlement[];
  /** Proposed class resolutions extracted from the will (will-only). */
  proposedClassResolutions?: import('@/lib/services/ai-analysis/types').PreparedClassResolution[];
  /**
   * The planning extraction (VALE-450) — present only on a recording that took
   * the **planning** route. A sibling of the admin arrays above, not a
   * replacement: `contacts` and `checklistItems` stay shared, while
   * `inventoryItems` is `[]` and `deceased` is absent.
   */
  planning?: RecordingPlanningExtractedData;
}

// ─── Planning extraction (VALE-450 → the VALE-466 review surface) ────
//
// The frontend mirror of `backend/src/ai-scribe/types/recording-planning.types.ts`.
// Read that file before changing anything here — it carries the reasoning; this
// is the wire shape only.
//
// **Two rules govern every consumer, and breaking either is silent:**
//
// 1. **Nothing here carries a database id.** Extraction runs before anything is
//    created — on an orphan recording the matter does not exist, and the partner
//    is usually not a contact yet. Every person is a `contactIndex` into *this
//    recording's own* `contacts[]` array, or an `unresolvedName`. Resolving an
//    index against any other array is LF-3750, where every entitlement silently
//    addressed the wrong list and the lawyer saw "nothing imported".
// 2. **Attribution is by SLOT, not by position.** A fact names its testator with
//    `client` / `partner`, so correcting a mis-heard spouse is a slot change —
//    which is exactly why the review surface can offer "move to the other
//    spouse" as one action.

/**
 * Which of the two people a planning matter is for.
 *
 * Aliased from the planning service rather than restated, so the two sides of
 * the same domain cannot drift: the extraction attributes by slot and the
 * planning record is keyed by it.
 */
export type AiScribePlanningTestatorSlot = PlanningTestatorSlot;

/**
 * A fact the meeting asked to take OFF the file ([EPLAN-22]).
 *
 * **Only ever explicit.** Removal is something the meeting says outright ("take
 * my brother off as executor"), never something inferred from a fact's absence:
 * a meeting that does not mention an executor is not removing them. So the
 * absence of this flag means "as instructed", and never "delete".
 *
 * On import a matched row is **retired, not deleted**. It stops reaching the
 * workspace, the merge fields and every drafted document, while the original
 * instruction stays on the file.
 */
export interface AiScribePlanningRemovable {
  /** The meeting asked for this to be removed. Absent means "as instructed". */
  removal?: true;
}

/** Shared person-reference fields. See rule 1 above on why nothing carries an id. */
export interface AiScribePlanningPersonRef extends AiScribePlanningRemovable {
  /** Index into **this recording's** `aiExtractedData.contacts[]`, and nothing else. */
  contactIndex?: number;
  /** The name as heard, when no extracted contact corresponds to them. */
  unresolvedName?: string;
  relationship?: string;
}

/**
 * What kind of planning meeting this was ([EPLAN-22]).
 *
 * **Not a flavour of `RecordingMeetingType`.** That is the classifier's routing
 * verdict (`admin` / `planning` / `mixed` / `other`) and answers "which
 * extraction should run". This answers "what was this planning meeting FOR",
 * and only exists on the planning route. The six values are the real corpus's
 * own taxonomy rather than an invented one.
 */
export type PlanningMeetingKind =
  /** A new client, full fact set, drafting follows. */
  | 'initial_instructions'
  /** Existing documents walked and changed. The most common kind, and the one that reconciles. */
  | 'review_and_update'
  /** Advice quoted and given before any draft exists. */
  | 'advice_first'
  /** One decision taken on a matter already under way. */
  | 'mid_matter_decision'
  /** The execution appointment itself. */
  | 'signing_appointment'
  /** The solicitor alone, dictating a note after the event. */
  | 'dictated_file_note';

/**
 * Where the meeting contradicts what the matter already holds ([EPLAN-22]).
 *
 * **Reported rather than resolved**: the model states both sides and where each
 * came from, and a human decides. The discrepancies are large in practice (an
 * eight-fold difference in a shareholding, in the design firm's own corpus), so
 * quietly preferring either side would be the wrong move about half the time.
 */
export interface AiScribePlanningConflict {
  /** Which testator it concerns, when it concerns one. */
  testatorSlot?: AiScribePlanningTestatorSlot;
  /** Plain-English name of what disagrees, such as "shareholding in Wilson Pty Ltd". */
  subject: string;
  /** What the matter already holds, in the words of the record. */
  existingValue: string;
  /** Where that came from, so the reviewer knows which side is the form and which is the room. */
  existingSource: PlanningFactSource;
  /** What the meeting said. */
  heardValue: string;
  /** Why the model thinks these disagree, and anything that helps settle it. */
  detail?: string;
  confidence?: ConfidenceLevel;
  source?: QuoteSource;
}

/** A role one testator appoints someone to, in their own instruments. */
export interface AiScribePlanningAppointment extends AiScribePlanningPersonRef {
  role: PlanningAppointmentRole;
  /** Array order is the appointment order when absent — substitutes follow their primary. */
  sortOrder?: number;
  notes?: string;
  confidence?: ConfidenceLevel;
  source?: QuoteSource;
}

/**
 * What one testator already holds of a given instrument.
 *
 * `hasExisting` is three-valued and the `null` is load-bearing: "I think I made
 * a will in the nineties" is the common answer, and flattening it to `false`
 * loses the question instead of putting it to the solicitor.
 */
export interface AiScribePlanningDocumentStatus {
  documentKind: PlanningDocumentKind;
  hasExisting?: boolean | null;
  madeInYear?: number | null;
  storageLocation?: string | null;
  supersedeIntent?: PlanningSupersedeIntent | null;
  /**
   * The meeting reviewed this instrument and it needs no change ([EPLAN-22]).
   *
   * A real deliverable, and until now indistinguishable from "not discussed",
   * which is the opposite conclusion: one says the firm has checked, the other
   * says nobody has.
   */
  reviewedNoChange?: boolean;
  notes?: string | null;
  confidence?: ConfidenceLevel;
  source?: QuoteSource;
}

/** One testator's wish, in their own words, unclassified. */
export interface AiScribePlanningWish extends AiScribePlanningRemovable {
  category?: string;
  text: string;
  sortOrder?: number;
  confidence?: ConfidenceLevel;
  source?: QuoteSource;
}

/** One of the two people a planning matter is for, with their own instructions. */
export interface AiScribePlanningTestator {
  slot: AiScribePlanningTestatorSlot;
  contactIndex?: number;
  unresolvedName?: string;
  /**
   * The diarisation label the model attributed this testator to (`spk_1`).
   * **Provenance for the reviewer, never a routing input** — it records *why*
   * the model split the facts the way it did, so a solicitor correcting a
   * mis-attributed spouse can see it was a diarisation call.
   */
  speakerLabel?: string;
  appointments: AiScribePlanningAppointment[];
  documentStatuses: AiScribePlanningDocumentStatus[];
  wishes: AiScribePlanningWish[];
  notes?: string;
  confidence?: ConfidenceLevel;
  source?: QuoteSource;
}

/** A child or dependant. `belongsToSlot` absent = shared, the common case. */
export interface AiScribePlanningDependant extends AiScribePlanningPersonRef {
  /** Set only for a child of one testator and not the other. Absent means shared. */
  belongsToSlot?: AiScribePlanningTestatorSlot;
  isDependant?: boolean;
  sortOrder?: number;
  notes?: string;
  confidence?: ConfidenceLevel;
  source?: QuoteSource;
}

/**
 * One clause of one testator's intended scheme.
 *
 * **`testatorSlot` is the field the planning apply reads** — not the
 * `planningTestatorSlot` it inherits from `PreparedEntitlement` (which belongs
 * to the intake path, [EPLAN-3]). Two names for one fact is a trap: an
 * attribution correction that wrote the inherited field would look right on
 * screen and land the clause on the wrong spouse's will.
 */
export interface AiScribePlanningEntitlement
  extends PreparedEntitlement, AiScribePlanningRemovable {
  testatorSlot: AiScribePlanningTestatorSlot;
  confidence?: ConfidenceLevel;
  source?: QuoteSource;
}

/** Why a proposed clause could not be carried into the reviewable set. */
export type PlanningEntitlementDropReason =
  /**
   * A `specific_gift` that never said **what is being given** — neither an item
   * link nor a description of the thing.
   *
   * Renamed from `specific_gift_needs_inventory` in [EPLAN-15], when planning
   * matters gained real inventory rows and gifts started linking them. The old
   * name meant "recorded as a wish instead", which is no longer a thing that
   * happens; what is left is the genuinely undraftable case.
   */
  | 'specific_gift_unlinkable'
  /** No `testatorSlot`, or one naming a testator the extraction did not propose. */
  | 'unattributed_testator'
  /** A beneficiary that is neither a resolvable `contactIndex` nor a class. */
  | 'unresolvable_beneficiary'
  /** Failed the shared master-shape validator (shares that do not reconcile). */
  | 'invalid_master_shape';

/**
 * A clause the solicitor should see was heard, even though it was not imported.
 *
 * **These must be rendered, never filtered out.** The alternative is the
 * LF-3750 failure mode: the row silently does not appear and "nothing was
 * proposed" is indistinguishable from "we could not carry this across".
 */
export interface AiScribeDroppedPlanningEntitlement {
  reason: PlanningEntitlementDropReason;
  /** Human-readable detail for the review callout — never an internal identifier. */
  detail: string;
  /** The raw row, so a reviewer can see what was said. */
  raw: Record<string, unknown>;
}

// ─── Structural facts: companies and trusts ([EPLAN-17]) ─────────────
//
// **Matter-scoped, and deliberately not assets.** One family trust is one trust
// recited in both wills, and a company is a separate legal person whose assets
// no will can give away. What the plan turns on is who *controls* each of them,
// which is why these sit beside the family section rather than inside a
// testator's column.
//
// The mirror of `PreparedPlanningCompanyDto` / `PreparedPlanningTrustDto` in
// `backend/src/ai-document-intelligence/dto/prepared-planning-facts.dto.ts`,
// plus the per-row provenance every Scribe planning shape carries (a meeting is
// heard rather than read, so the reviewer needs to see how sure the model was).

/**
 * A person named on a company or trust.
 *
 * Carries `name` outright rather than only an index, because most people on a
 * client's structures **are** the clients: `testatorSlot` names them when the
 * source said so, `contactIndex` is the usual index hint into this recording's
 * own `contacts[]`, and the name is what apply falls back to.
 */
export interface AiScribePlanningStructurePerson {
  /** The name as the meeting gave it. Always present, unlike every other person ref here. */
  name: string;
  /** Index into **this recording's** `aiExtractedData.contacts[]`, and nothing else. */
  contactIndex?: number;
  /** Set when this person is one of the two testators. */
  testatorSlot?: PlanningTestatorSlot;
}

/** One person's involvement in a company: what they are, and what they hold. */
export interface AiScribePlanningCompanyInterest extends AiScribePlanningStructurePerson {
  isDirector?: boolean;
  isSecretary?: boolean;
  sharesHeld?: number;
  shareClass?: string;
}

/** A company the clients control. Not an asset: see the section note above. */
export interface AiScribePlanningCompany {
  name: string;
  acn?: string;
  abn?: string;
  issuedShareCount?: number;
  /**
   * **Three-valued, and `null` means nobody has checked.** That is NOT "no".
   *
   * A sole director is why a corporate power of attorney gets recommended: if
   * the only director loses capacity, nobody can act for the company at all. A
   * source naming one director has not established there is only one, so the
   * honest answer is usually `null`, and a surface that renders it as an
   * unticked box answers the question on the firm's behalf.
   */
  isSoleDirector?: boolean | null;
  interests?: AiScribePlanningCompanyInterest[];
  notes?: string;
  confidence?: ConfidenceLevel;
  source?: QuoteSource;
}

/** One person's involvement in a trust, and what it owes them personally. */
export interface AiScribePlanningTrustInterest extends AiScribePlanningStructurePerson {
  isTrustee?: boolean;
  /** Can replace the trustee, that is, controls the trust. */
  isAppointor?: boolean;
  isSuccessorAppointor?: boolean;
  isBeneficiary?: boolean;
  /**
   * Money the trust already owes this person and has not paid.
   *
   * **It forms part of their personal estate even though the trust's own assets
   * do not**, which is why it is recorded per person rather than on the trust.
   */
  unpaidPresentEntitlement?: number;
}

/** A trust the clients control or benefit from. */
export interface AiScribePlanningTrust {
  name: string;
  kind?: PlanningTrustKind;
  deedDate?: string;
  /** Named rather than referenced: this proposal has no ids yet. */
  corporateTrusteeName?: string;
  /** Whether the deed or a will decides who takes the appointor's power on death. */
  appointorSuccession?: PlanningAppointorSuccession;
  appointorSuccessionNote?: string;
  balanceDate?: string;
  interests?: AiScribePlanningTrustInterest[];
  notes?: string;
  confidence?: ConfidenceLevel;
  source?: QuoteSource;
}

/**
 * One owner of a planning asset, as the extraction proposes it ([EPLAN-17]).
 *
 * Three ways to name the person, in falling order of certainty: a testator
 * **slot** where the source says it is one of the two people whose wills are
 * being drafted; a `contactIndex` where it is somebody else being imported in
 * the same pass; and a bare `name` otherwise.
 *
 * The *mode* is not here. `solely-held` / `joint-tenants` / `tenants-in-common`
 * rides in `itemInfo.ownershipType`; this carries only WHOSE.
 */
export interface AiScribePlanningAssetOwner {
  testatorSlot?: PlanningTestatorSlot;
  contactIndex?: number;
  name?: string;
  /** Percentage, for tenants in common. Omitted for joint proprietors, who hold no divided share. */
  sharePercentage?: number;
}

export interface RecordingPlanningExtractedData {
  /**
   * What kind of planning meeting this was ([EPLAN-22]) — **the model's
   * proposal only**.
   *
   * The durable answer lives on `Recording.planningMeetingKind`, with a
   * `planningMeetingKindSetBy` sibling, because a lawyer's correction has to
   * survive both a re-analysis (which overwrites this blob) and "revert to AI"
   * (which restores the model's own answer). Read the recording's field to
   * display the kind; this one is carried so the proposal reaches the review
   * surface with everything else the meeting proposed.
   */
  meetingKind?: PlanningMeetingKind;
  /**
   * Where the meeting contradicts what the matter already holds ([EPLAN-22]).
   *
   * The highest-value thing this extraction produces, and the one the summary
   * could only ever state as a sentence nothing could act on.
   */
  conflicts?: AiScribePlanningConflict[];
  /** The two testator slots and their per-person facts. One entry for a single-client matter. */
  testators: AiScribePlanningTestator[];
  /** Children and dependants, **matter-scoped** — a shared child is one row, not one per spouse. */
  dependants: AiScribePlanningDependant[];
  /** The scheme each testator intends, as draft entitlements awaiting review. */
  entitlements: AiScribePlanningEntitlement[];
  droppedEntitlements?: AiScribeDroppedPlanningEntitlement[];
  /**
   * The companies the clients control ([EPLAN-17]).
   *
   * Optional, like `droppedEntitlements`: every block written before that ticket
   * has no such key, and a required array would make those rows read as
   * malformed rather than as silent on the question.
   */
  companies?: AiScribePlanningCompany[];
  /** The trusts the clients control or benefit from. Matter-scoped, and not assets either. */
  trusts?: AiScribePlanningTrust[];
}

/**
 * What a proposed planning row would DO to the matter ([EPLAN-22]).
 *
 * A repeat estate-planning meeting restates most of what the file already
 * holds, so most of a review meeting's list is confirmation rather than news.
 * These are what let the surface be quiet about the confirmations and loud
 * about the four rows that actually move.
 */
export type PlanningFactVerb = 'new' | 'unchanged' | 'changed' | 'removed';

/**
 * One row's verb, addressed by its position in the **stored** planning block.
 *
 * `path` is `client:appointment:0` · `partner:wish:2` · `dependant:1` ·
 * `clause:0`, deliberately not the review's own local row id, which is
 * generated client-side and which the server has never seen. The join happens
 * once, at ingest, while the two are still in step. See
 * `localIdForChangeSetPath` in `planning-review-model.ts`.
 *
 * **Server-derived on every read and never stored**: the matter's facts move
 * independently of the recording, so a stored verb would go on calling
 * something new after a colleague had already added it.
 */
export interface PlanningChangeSetEntry {
  path: string;
  verb: PlanningFactVerb;
  /** The existing row this pairs with. Absent on `new`, and on a `removed` matching nothing. */
  matchedId?: number;
  /** Named on `changed`, so a row can say what moved rather than only that something did. */
  changedFields?: string[];
}

/**
 * What the planning apply did (VALE-450) — a discriminated outcome, because a
 * planning apply that does not run must say so rather than report zero counts.
 */
export type PlanningApplySkipReason =
  | 'already_imported'
  | 'matter_type_unsupported'
  | 'planning_not_enabled';

export type PlanningApplyOutcome =
  | {
      status: 'applied';
      testators: number;
      appointments: number;
      documentStatuses: number;
      wishes: number;
      dependants: number;
      entitlementsCreated: number;
      dropped: Array<{
        reason: PlanningEntitlementDropReason;
        detail: string;
      }>;
    }
  | { status: 'skipped'; reason: PlanningApplySkipReason }
  | { status: 'failed'; detail: string };

// ─── Consolidation Progress ────────────────────────────────────────

export interface ConsolidationProgress {
  status: 'not_started' | 'processing' | 'completed' | 'failed';
  overallProgress: number;
  consolidationSteps: string[];
  message: string | null;
}

/** Unimported intake form available for consolidation */
export interface UnimportedIntakeForm {
  id: string;
  contactName: string;
  status: 'submitted' | 'in_review';
  submittedAt: string | null;
}

export interface LawyerNotes {
  contacts?: string;
  assets?: string;
  liabilities?: string;
  clientInstructions?: string;
}

/** Extends Recording with heavy fields excluded from list views */
export interface RecordingDetail extends Recording {
  // ─── LF-3743: handoff polling fields ───────────────────────────
  // Drive the desktop "Record on phone" sheet's phase rendering.
  // Derived BE-side in RecordingsService.toResponseDto. `null` for
  // non-handoff recordings (the existing desktop flow).
  /**
   * Sheet view to render. `awaiting_scan` → QR + countdown;
   * `scan_expired` → Regenerate CTA; `recording` → "Recording on your
   * phone" (may persist for hours legitimately, BE sweeps at 48h);
   * `uploaded` → existing processing UI takes over.
   */
  handoffPhase?:
    | 'awaiting_scan'
    | 'scan_expired'
    | 'recording'
    | 'uploaded'
    | null;
  /** ISO — when mobile first claimed the handoff. Drives "Recording for {elapsed}" copy. */
  handoffClaimedAt?: string | null;
  /** ISO — deadline for the 2-min unclaimed scan window. Ignored once claimed. */
  handoffExpiresAt?: string | null;

  transcript: RecordingTranscript | null;
  aiSummary: RecordingAiSummary | null;
  aiExtractedData: RecordingAiExtractedData | null;
  /**
   * Immutable LLM baseline of `aiExtractedData` — what the model emitted at the
   * last LLM pass. Powers per-row "Revert to AI" UX on the unified review
   * surface (LF-3052). Reanalyse refreshes it; PATCH and apply-changes never
   * touch it. Optional — legacy recordings analysed before LF-3052 won't have
   * it populated.
   */
  aiExtractedDataLlm?: RecordingAiExtractedData | null;
  /**
   * Frozen historical projection of the live preview-changes response,
   * captured pre-mutation at apply time and overlaid with the lawyer's
   * apply-DTO edits (LF-3545). Source of truth for the read-only post-import
   * review surface — replaces the conflated dual-purpose use of
   * /preview-changes for imported recordings. Null for legacy imports
   * (pre-LF-3545) and when apply-time capture failed — consumers fall back
   * to a synthesised view from `aiExtractedData`.
   */
  appliedPreviewSnapshot?: PreviewChangesResponse | null;
  lawyerNotes: LawyerNotes | null;
  consolidationRequested: boolean;
  errorMessage: string | null;
  unimportedIntakeForms: UnimportedIntakeForm[] | null;
  documentCount: number;
  /**
   * What each proposed planning row would do to the matter ([EPLAN-22]).
   *
   * **Detail-read only, and `null` is a real answer.** It is `null` on an
   * orphan recording (there is no matter to reconcile against) and on one with
   * no planning extraction, and the server also returns an empty array rather
   * than failing when the baseline cannot be read. So a surface reading this
   * must render perfectly well without it: a missing verb means "we could not
   * say", never "unchanged".
   */
  planningChangeSet?: PlanningChangeSetEntry[] | null;
  /**
   * The enquiry this recording was captured for (VALE-986). `null` for every
   * recording that was not started from one.
   */
  lead?: RecordingLeadContext | null;
}

/**
 * The enquiry a recording was captured for, as the session screen states it
 * (VALE-986).
 *
 * **Every value is resolved server-side, and none of it may be re-derived here.**
 * Which of an enquiry's meetings the product means has one definition
 * (`LeadDerivationInputBuilder.selectRenderedMeeting`) and the enquiry's names
 * have one composition, so the recording screen, the enquiry drawer's Meeting
 * card and the enquiry board all describe the same appointment and spell the same
 * names. That is the leads module's single most important structural rule; this
 * shape is how the Scribe surface inherits it rather than re-implementing it —
 * which is also why it carries a rendered `enquiryTypeLabel` and a solicitor's
 * *name* rather than a slug and an id to look up.
 *
 * Nullable throughout because an enquiry is legitimately empty: capture asks for
 * a first name and nothing else, so the deceased is often unknown on the first
 * call and the meeting is often booked later.
 */
export interface RecordingLeadContext {
  id: number;
  /** The deceased, composed. `null` when the enquiry has not captured one. */
  deceasedName: string | null;
  /** The enquirer(s), joined with ` & ` when a second one was captured. */
  clientNames: string;
  /** The enquirer's relationship **to** the deceased, on the shared value set. */
  relationshipToDeceased: string | null;
  /** The enquiry's type, in words — already rendered, never a slug. */
  enquiryTypeLabel: string;
  /** ISO — when the rendered meeting starts. `null` when none is recorded. */
  meetingStartsAt: string | null;
  /** The rendered meeting's booked solicitor, by name. */
  meetingBookedSolicitorName: string | null;
}

// ─── Paginated List Response ────────────────────────────────────────

export interface PaginationMeta {
  totalItems: number;
  itemCount: number;
  itemsPerPage: number;
  totalPages: number;
  currentPage: number;
}

export interface RecordingListResponse {
  items: Recording[];
  meta: PaginationMeta;
}

/**
 * Query params for the firm-wide / matter-scoped recording list
 * (`GET /recordings`, LF-3712). All optional — the default scope is firm-wide,
 * active (`archivedAt` null), recent-first.
 */
export interface RecordingListParams {
  /** Full-text search over transcript + summary + title (ranked). */
  q?: string;
  /** Lifecycle status filter — any subset of RecordingStatus. */
  status?: RecordingStatus[];
  /** `true` returns only archived recordings; default (`false`) hides them. */
  archived?: boolean;
  /** Scope to a single matter (the matter-detail recordings window). */
  matterId?: number;
  mode?: RecordingMode;
  page?: number;
  limit?: number;
}

// ─── Preview Changes Types ──────────────────────────────────────────

// Shared DTOs: `FieldChange`, `PreviewDeceased`, `PreviewDeceasedAddress`,
// `PreviewMatterDiff` live in `lib/services/ai-analysis/types.ts` after
// LF-2910. Re-export here so existing `@/lib/services/recordings` imports
// continue to work.
export type {
  FieldChange,
  PreviewDeceasedAddress,
  PreviewDeceased,
  PreviewMatterDiff
} from '@/lib/services/ai-analysis/types';

import type {
  FieldChange,
  PreviewMatterDiff
} from '@/lib/services/ai-analysis/types';

export interface PreviewContactDiff {
  action: 'create' | 'skip' | 'update';
  proposed: ProposedContact;
  skipReason?: string;
  matchedExistingId?: number;
  matchConfidence?: MatchConfidence;
  /** LF-3330 — where the match decision came from. */
  matchSource?: import('@/lib/services/ai-analysis/types').MatchSource;
  /** LF-3330 — LLM rationale for the hint. Carried on the wire; no longer surfaced in the UI (VALE-720 removed the match tag). */
  matchReasoning?: string;
  fieldChanges?: Record<string, FieldChange>;
  existingSnapshot?: Partial<ProposedContact>;
  /** LF-5242 — latent "separate as a new contact" payload; present on a matched row only when the match is not an exact name/email identity match. */
  separateAsCreate?: { proposed: ProposedContact };
  confidence?: ConfidenceLevel;
  source?: QuoteSource;
}

export interface PreviewInventoryItemDiff {
  action: 'create' | 'skip' | 'update';
  proposed: ProposedInventoryItem;
  skipReason?: string;
  matchedExistingId?: number;
  matchConfidence?: MatchConfidence;
  /** LF-3330 — where the match decision came from. */
  matchSource?: import('@/lib/services/ai-analysis/types').MatchSource;
  /** LF-3330 — LLM rationale for the hint. Carried on the wire; no longer surfaced in the UI (VALE-720 removed the match tag). */
  matchReasoning?: string;
  fieldChanges?: Record<string, FieldChange>;
  existingSnapshot?: Partial<ProposedInventoryItem>;
  instructionType?: InstructionType;
  confidence?: ConfidenceLevel;
  source?: QuoteSource;
}

export interface PreviewChecklistItem {
  text: string;
  assignee: ActionItemAssignee;
  instructionType?: InstructionType;
  confidence?: ConfidenceLevel;
  source?: QuoteSource;
}

export interface PreviewExistingItemNote {
  entityType: 'contact' | 'inventoryItem';
  matchedEntityId: number;
  matchedEntityName: string;
  suggestedNote: string;
  instructionType?: InstructionType;
  confidence?: ConfidenceLevel;
  source?: QuoteSource;
}

export interface PreviewChangesSummary {
  contacts: { new: number; updated: number; skipped: number };
  inventoryItems: { new: number; updated: number; skipped: number };
  checklistItems: { new: number };
  matter: { updated: boolean };
}

export interface PreviewChangesResponse {
  contacts: PreviewContactDiff[];
  inventoryItems: PreviewInventoryItemDiff[];
  checklistItems: PreviewChecklistItem[];
  existingItemNotes: PreviewExistingItemNote[];
  /** Absent when the transcript had no deceased info. */
  matter?: PreviewMatterDiff;
  summary: PreviewChangesSummary;
  /** AI-proposed entitlements extracted from the will (may be empty). */
  entitlements?: import('@/lib/services/ai-analysis/types').PreparedEntitlement[];
  /** Signals the matter already has entitlements; accept flow should be blocked. */
  entitlementsPreviewWarning?:
    | import('@/lib/services/ai-analysis/types').EntitlementsPreviewWarning
    | null;
  /** Class-resolution proposals (will-only). Omitted when empty. */
  proposedClassResolutions?: import('@/lib/services/ai-analysis/types').PreparedClassResolution[];
  /**
   * The planning extraction, **passed through verbatim — this is not a diff**
   * (VALE-450). A planning fact has no matcher and no dedup pipeline (the write
   * contract appends; document statuses upsert on `(testator, kind)`), so the
   * reviewable proposal *is* the extraction. Absent on the admin route.
   */
  planning?: RecordingPlanningExtractedData;
  /** Handovers the meeting stated (VALE-1866). Proposals only; omitted when none. */
  proposedHandovers?: import('@/lib/services/matter-handling/types').PreviewProposedHandover[];
  /** Contacts already on the matter a reviewer may pick as a handler. */
  handoverHandlerOptions?: import('@/lib/services/matter-handling/types').HandoverHandlerOption[];
  /** Handovers the matter already records, on the rows they cover. */
  currentHandovers?: import('@/lib/services/matter-handling/types').CurrentHandover[];
}

// ─── Apply Changes Types ────────────────────────────────────────────

export interface PostalAddress {
  addressLine1?: string;
  addressLine2?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  country?: string;
}

export interface ProposedContact {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  organisationName?: string;
  email?: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  address?: PostalAddress;
  notes?: string;
  matterNotes?: string;
  isLegalPersonalRepresentative: boolean;
  isBeneficiary: boolean;
  legalPersonalRepresentativeRole?: LegalPersonalRepresentativeRoleSlug;
  relationshipToDeceased?: string;
  isOfAge?: boolean;
}

export interface InstitutionPreviewRef {
  id: number;
  name: string;
  type: string;
  faviconUrl?: string;
}

export interface ProposedInventoryItem {
  type: string;
  institutionId?: number;
  institution?: InstitutionPreviewRef;
  estateReference?: string;
  isNonEstate?: boolean;
  itemInfo?: Record<string, unknown>;
}

export interface ApplyContact {
  action: 'create' | 'update';
  proposed: ProposedContact;
  matchedExistingId?: number;
  /** Echoed from the preview diff row (LF-5681) so a contact-alias identity variant persists the full QuoteSource. */
  confidence?: ConfidenceLevel;
  source?: QuoteSource;
}

export interface ApplyInventoryItem {
  action: 'create' | 'update';
  proposed: ProposedInventoryItem;
  matchedExistingId?: number;
  /** Echoed from the preview diff row (LF-5681) so an SRN/HIN identity variant persists the full QuoteSource. */
  confidence?: ConfidenceLevel;
  source?: QuoteSource;
}

export interface ApplyChecklistItem {
  text: string;
  assignee?: ActionItemAssignee;
  /**
   * LF-3052: lawyer can pre-mark items already done during review. Defaults
   * to false. Surfaces as the matter checklist item's initial completion
   * state on import.
   */
  isComplete?: boolean;
  /**
   * VALE-1703: the extraction's classification of this instruction, carried
   * through review unchanged so it can be stored on the matter's checklist
   * item. **This is the transport**: the import boundary has no other way to
   * recover it, because the lawyer may have edited the row's text. Absent on a
   * row the lawyer added during review.
   */
  instructionType?: InstructionType;
}

export interface ApplyExistingItemNote {
  entityType: 'contact' | 'inventoryItem';
  matchedEntityId: number;
  suggestedNote: string;
}

export interface ApplyDeceasedAddress {
  streetAddress?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  country?: string;
}

export interface ApplyMatterDeceased {
  title?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  /** ISO YYYY-MM-DD */
  dateOfBirth?: string;
  /** ISO YYYY-MM-DD */
  dateOfDeath?: string;
  /** Merge-semantic on the backend — provided fields are merged onto existing.decedentAddress. */
  address?: ApplyDeceasedAddress;
}

export interface ApplyChangesRequest {
  contacts?: ApplyContact[];
  inventoryItems?: ApplyInventoryItem[];
  checklistItems?: ApplyChecklistItem[];
  existingItemNotes?: ApplyExistingItemNote[];
  /** Omit when the reviewer rejected all matter changes or the preview had no `matter` block. */
  deceased?: ApplyMatterDeceased;
  /** Matter-level free-text notes. Writes to `Matter.notes`. */
  notes?: string;
  /** Reviewed entitlement proposals (same shape as preview's PreparedEntitlement[]). */
  entitlements?: import('@/lib/services/ai-analysis/types').PreparedEntitlement[];
  /** Accepted class resolutions — see ai-analysis types. */
  acceptedClassResolutions?: import('@/lib/services/ai-analysis/types').PreparedClassResolution[];
  /** Proposed handovers the reviewer accepted (VALE-1866). */
  acceptedHandovers?: import('@/lib/services/matter-handling/types').ApplyProposedHandover[];
}

export interface ApplyChangesResponse {
  recordingId: number;
  applied: {
    contacts: { created: number; updated: number };
    inventoryItems: { created: number; updated: number };
    checklistItems: { added: number };
    existingItemNotes: { applied: number };
    matter: { updated: boolean };
    entitlements?: { created: number };
    classResolutions?: { applied: number };
    /** Absent when no handover was accepted (VALE-1866). */
    handovers?: import('@/lib/services/matter-handling/types').HandoversApplyOutcome;
    /**
     * Present whenever the recording carried a planning extraction (VALE-450),
     * absent on the admin route. **Report a `skipped` / `failed` outcome to the
     * solicitor** — the import as a whole still succeeds, so staying quiet here
     * is indistinguishable from "there was nothing to import".
     */
    planning?: PlanningApplyOutcome;
  };
}

// ─── Client telemetry ───────────────────────────────────────────────

/**
 * Write-once diagnostic payload describing the browser / device / codec
 * that produced a recording. Captured at recording-create time. Not
 * lawyer-visible — internal support data, surfaced only in admin tooling.
 *
 * Field-by-field rationale lives in
 * `.claude/tasks/20260522-ios-mp4-chunking-fix/backend-brief.md`.
 */
export interface ClientTelemetry {
  userAgent: string;
  platform: string;
  recorderMimeType: string;
  chunkingStrategy: 'timeslice-1000' | 'manual-request-data-1000' | 'unknown';
  recorderStartedAt: string;
  audioInputLabel?: string;
  audioInputDeviceId?: string;
  sampleRate?: number;
  /**
   * Structured device platform, parsed from the UA by the shared
   * `lib/utils/parse-platform` (VALE-212). Populated for iOS/iPadOS, Android,
   * Windows, macOS and Linux — before VALE-212 only `iosVersion` below was
   * captured, so every Android recording's telemetry landed with a blank OS.
   */
  os?: string;
  osVersion?: string;
  /** Browser family + major version, e.g. `Safari 18`, `Samsung Internet 23`. */
  browser?: string;
  /**
   * Legacy iOS-family version, kept populated from `osVersion` for continuity
   * with historical rows. Prefer `os` + `osVersion` for anything new.
   */
  iosVersion?: string;
  clientVersion?: string;
}

/**
 * Telemetry only known once recording has stopped and chunks have been
 * assembled (Option C in the backend brief). Sent on upload-complete.
 *
 * `hadResume` / `retryCount` (LF-3743) are surfaced here, NOT at the top
 * level of the upload-complete body — the BE DTOs (both desktop and
 * handoff) treat the body as strict whitelist + nest mobile-specific
 * telemetry under `clientTelemetryAtUpload`. Both fields default to
 * `false` / `0` for the desktop happy path.
 */
export interface ClientTelemetryAtUpload {
  chunkCount: number;
  connectionType?: string;
  hadResume?: boolean;
  retryCount?: number;
}

// ─── Request DTOs ───────────────────────────────────────────────────

export interface CreateRecordingRequest {
  /**
   * Matter the recording belongs to. Optional — omit to create an orphan
   * (matterless) recording from the top-level Scribe surface (LF-3712).
   */
  matterId?: number;
  /**
   * Lead the recording belongs to — a pre-matter enquiry's first consultation
   * (LF-5995). Mutually exclusive with `matterId`: the backend 400s the pair,
   * because an artefact belongs to the estate or to the enquiry, not both.
   *
   * A lead-linked create is **find-or-create per lead**, so `Record the meeting`
   * can be pressed twice without leaving a second empty draft behind.
   */
  leadId?: number;
  /**
   * Create the recording in the `draft` state (top-level session page) — nothing
   * captured yet. Omit for the matter-bound capture flow (created at record-start
   * in `uploading`).
   */
  isDraft?: boolean;
  consentConfirmedAt: string;
  recordedAt: string;
  title?: string;
  participantContactIds?: number[];
  /** Free-text participant "speaker hint" names (not contact entities). */
  participantNames?: string[];
  /** Free-text lawyer-provided context/instructions. */
  instructions?: string;
  audioFormat?: AudioFormat;
  mode?: RecordingMode;
  /** S3 key of a pre-uploaded audio file. When provided, audioFormat is not required. */
  audioS3Key?: string;
  /** Diagnostic snapshot of the recording client — see ClientTelemetry. */
  clientTelemetry?: ClientTelemetry;
}

export interface AudioUploadUrlRequest {
  /** Matter the recording belongs to. See CreateRecordingRequest. */
  matterId: number;
  audioFormat: AudioFormat;
}

export interface AudioUploadUrlResponse {
  uploadUrl: string;
  audioS3Key: string;
}

export interface UploadCompleteRequest {
  duration: number;
  fileSize: number;
  /** Telemetry observed only after recording stops — see ClientTelemetryAtUpload. */
  clientTelemetryAtUpload?: ClientTelemetryAtUpload;
  /**
   * Truthful audio format of the uploaded bytes — read from
   * `MediaRecorder.mimeType` after construction. Safety-net field for the
   * iOS Safari case where the pre-start `isTypeSupported` probe lied and
   * the BE row was created with the wrong format (LF-3780). When this
   * differs from the row's stored audioFormat, the BE is expected to
   * rename the S3 object + update the row before invoking Transcribe.
   * Optional pending BE rollout.
   */
  audioFormat?: 'webm' | 'mp4';
}

export interface UpdateRecordingRequest {
  title?: string;
  participantContactIds?: number[];
  /** Free-text participant "speaker hint" names — replaces the stored array when provided. */
  participantNames?: string[];
  /** Free-text lawyer-provided context/instructions — editable any time. */
  instructions?: string;
  lawyerNotes?: LawyerNotes;
  /**
   * Destructive transcript edit (LF-3712, D-31). The provided transcript
   * REPLACES the stored one verbatim — no original is kept — and the server
   * refreshes the search vector. Speaker relabelling rides this same edit.
   * Allowed only when status is `ready` or `imported`.
   */
  transcript?: {
    fullText: string;
    segments?: TranscriptSegment[];
  };
  /**
   * Lawyer-edited summary sections (VALE-480). **Replace-full-array**: the
   * provided `sections` REPLACES the stored array, so an edit to one section
   * still sends every section back. Omit to leave the summary untouched.
   * Allowed only when status is `ready` or `imported`.
   */
  aiSummary?: {
    sections?: RecordingAiSummarySection[];
  };
  /**
   * Inline edits to extracted entities from the unified review surface
   * (LF-3052). Each sub-array, when present, REPLACES the matching array on
   * the recording — the FE always sends the full edited array. Allowed only
   * when status is `ready`; `imported` recordings are locked.
   *
   * Entitlements / proposedClassResolutions are not currently edited inline —
   * they flow through preview-changes and are reviewed in the apply payload,
   * not via this PATCH.
   */
  aiExtractedData?: {
    contacts?: AiScribeExtractedContact[];
    inventoryItems?: AiScribeExtractedInventoryItem[];
    checklistItems?: AiScribeExtractedChecklistItem[];
    existingItemNotes?: AiScribeExistingItemNote[];
    deceased?: AiScribeExtractedDeceased | null;
    /**
     * The planning extraction block (VALE-450). **Replaces the whole object** —
     * the review surface holds the full edited state and sends it back, because
     * a deep merge would make a removed appointment un-removable. Send `null` to
     * clear it.
     *
     * The server rejects a block missing any of `testators` / `dependants` /
     * `entitlements` (they are iterated unguarded at apply time), so always send
     * all three arrays even when empty.
     */
    planning?: RecordingPlanningExtractedData | null;
  };
  /**
   * What kind of planning meeting this was ([EPLAN-22]).
   *
   * The analysis pass proposes one; sending it here is the lawyer correcting
   * that, and their answer is never overwritten by a later re-analysis. It is a
   * field on the recording rather than part of `aiExtractedData.planning`, for
   * exactly that reason. Distinct from `meetingType`, the classifier's routing
   * verdict, which is not settable here.
   */
  planningMeetingKind?: PlanningMeetingKind;
}

// ─── IndexedDB Types ────────────────────────────────────────────────

/** Schema for audio chunks stored in IndexedDB during recording */
export interface StoredAudioChunk {
  recordingId: number;
  matterId: number;
  matterName: string;
  chunkIndex: number;
  /**
   * The chunk's raw bytes. Stored as an ArrayBuffer, NOT a Blob: on iOS 18.7+
   * a MediaRecorder Blob kept as an IndexedDB value reads back empty (its
   * backing is released across the round-trip), corrupting reassembly. The
   * bytes are materialised via `event.data.arrayBuffer()` at emit time. Blob
   * assembly still accepts these directly (`new Blob([arrayBuffer], …)`).
   * (LF-5157)
   */
  data: ArrayBuffer;
  mimeType: string;
  totalDuration: number;
  createdAt: number;
}

/** Upload metadata persisted in IndexedDB alongside audio chunks.
 *  Survives tab close / crash so retries can use the correct recording ID and upload URL. */
export interface StoredRecordingMeta {
  /** Server recording ID — key for this store */
  recordingId: number;
  matterId: number;
  /** S3 presigned upload URL (may be expired — refreshed via regenerateUploadUrl) */
  uploadUrl: string;
  mimeType: string;
  // ─── LF-3743: handoff fields ──────────────────────────────────
  /**
   * Which surface produced this recording. `'mobile'` rows go through the
   * QR-handoff recovery flow and observe the 1h post-error retention rule.
   * Undefined for legacy / desktop rows.
   */
  handoffSource?: 'desktop' | 'mobile';
  /**
   * ISO — set when `upload-complete` returns 200. Used by the handoff
   * recovery flow to detect "upload succeeded but upload-complete didn't
   * fire, re-fire it (idempotent)". Undefined until upload completes.
   */
  uploadedAt?: string;
}

/** Orphaned recording detected on app load (recovery flow) */
export interface OrphanedRecording {
  recordingId: number;
  matterId: number;
  matterName: string;
  chunkCount: number;
  totalDuration: number;
  createdAt: Date;
  isExpired: boolean;
}
